import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../Button/Button";
import Modal from "../../Modal";
import * as api from "../../../api";
import "./index.css";
import {
  FinancialReferralReasonRes,
  clearReferralInformation,
  comingFromReferralPage,
  saveReferralReasonReferenceData,
} from "../../../ducks/redux/actions/FinancialReferral";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import LoadingSpinner from "../../../storybook/LoadingSpinner";

interface ReferralReasonProps extends RouteComponentProps<any> {}

const ReferralReason: React.FC<ReferralReasonProps> = (props: any) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const data = useSelector(
    (state: any) => state.financialReferralReducer?.financialReferralData,
  );
  const referralReasonReferenceData = useSelector(
    (state: any) => state.financialReferralReducer?.referralReasonReferenceData,
  );

  const [referralReasons, setReferralReasons] = useState<string[]>([]);
  const [reasonChecked, setReasonChecked] = useState<string[]>(
    Array.isArray(data?.ReferralReason) ? data?.ReferralReason : [],
  );
  const [isLoader, setIsLoader] = useState(true);
  const [error, setError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const hasReferralReasons = (referralReasons?.length || 0) > 0;

  useEffect(() => {
    if (!data?.referralFor) {
      props.history.push("/CustomerConsent");
      return;
    }

    const buildReferralReasons = (referenceDataToUse: any) => {
      const keyValuePairs = referenceDataToUse?.[0]?.namespace?.keyValues;
      const arrData: string[] = [];

      for (const _data of Object.values(keyValuePairs || {})) {
        const obj: any = _data as any;

        const isAllowedInP360 =
          Array.isArray(obj?.allowedChannels) &&
          obj.allowedChannels.includes("P360");

        if (isAllowedInP360 && obj?.referralReason) {
          arrData.push(obj.referralReason);
        }
      }

      arrData.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      setReferralReasons(arrData);

      if (Array.isArray(data?.ReferralReason)) {
        setReasonChecked(data?.ReferralReason);
      }
    };

    const fetchReferralReason = async () => {
      try {
        let referenceDataToUse: any =
          Array.isArray(referralReasonReferenceData) &&
          referralReasonReferenceData.length > 0
            ? referralReasonReferenceData
            : null;

        if (!referenceDataToUse) {
          const responseData = await api.getFinancialReferralReasonData();
          referenceDataToUse = responseData;
          dispatch(saveReferralReasonReferenceData(responseData));
        }

        if (referenceDataToUse) {
          buildReferralReasons(referenceDataToUse);
        }
      } catch (e: any) {
        console.log("Error:", e);
      } finally {
        setIsLoader(false);
      }
    };

    fetchReferralReason();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exitButtonHandler = () => {
    dispatch(clearReferralInformation({}));
    props.history.push("/Customer360View");
  };

  const onBackHandler = () => {
    props.history.push("/CustomerConsent");
  };

  const onContinueHandler = () => {
    if (!hasReferralReasons) {
      return;
    }
    if (!reasonChecked || reasonChecked.length === 0) {
      setError(true);
      return;
    }

    dispatch(FinancialReferralReasonRes(reasonChecked));
    dispatch(comingFromReferralPage(true));
    if (reasonChecked.includes("Debt")) {
      props.history.push("/referraldebtquestion");
    } else {
      props.history.push("/nospform");
    }
  };

  const handleChange = (event: any) => {
    setError(false);

    const updated = [...(reasonChecked || [])];

    if (event?.target?.checked) {
      if (!updated.includes(event?.target?.name)) {
        updated.push(event?.target?.name);
      }
    } else {
      const index = updated.indexOf(event?.target?.name);
      if (index > -1) {
        updated.splice(index, 1);
      }
    }

    setReasonChecked(updated);
    dispatch(FinancialReferralReasonRes(updated));
  };
  const isNonInputTarget = (e: any) =>
    (e?.target as HTMLElement | null)?.tagName !== "INPUT";

  const onReasonLabelClick = (e: any) => {
    if (isNonInputTarget(e)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onReasonLabelMouseDown = (e: any) => {
    if (isNonInputTarget(e)) {
      e.preventDefault();
    }
  };
  
  return (
    <div className="container sm-ml2-mr1 ctn-fht" id="referalreason-container" data-testid="referalreason-container">
      <div id="backmodal-div1" className="mb-2 md:mb-2 lg:mb-2">
        <p id="backmodal-p" className="text-teal text-xl mt-4 mb-0">
          <button
            data-testid="custBackBtn"
            className="back-btn"
            onClick={onBackHandler}
          >
            <i className="fas fa-angle-left"></i>
            <span>{t("back")}</span>
          </button>
        </p>
      </div>
      <div className="sm-ml2-mr1">
        <div className="h5 ms-0 ms-md-2" id="repreason-div1">
          <h1 className="mt-0 text-purple-100 text-2xl mb-3 global-txt">
            {t("Referral_Reason")}
          </h1>
        </div>
        {isLoader ? (
          <div className="div-loader marign-left-smallscreen-0">
            <div className="SpinnerWrapper" id="SpinnerWrapperRefrealReason">
              <LoadingSpinner />
            </div>
          </div>
        ) : (
          <div className="ref-gap-wrapper">
            {error && (
              <p className="ms-0 mb-0 ms-md-2 referral-reason-error-text">
                {t("Financial_DebtQuestion_Error_SelectReasons")}
              </p>
            )}

            <p className="my-0 fw-bold ms-0 ms-md-2">
              {t("Financial_DebtQuestion_TickAll")}
            </p>

            <div className={error ? "referral-reason-error" : ""}>
              <Box data-testid="reasondata" className="mb-3">
                <div className="row">
                  {(() => {
                    const chunkSize = Math.ceil((referralReasons || []).length / 3);
                    const col1 = (referralReasons || []).slice(0, chunkSize);
                    const col2 = (referralReasons || []).slice(
                      chunkSize,
                      chunkSize * 2,
                    );
                    const col3 = (referralReasons || []).slice(chunkSize * 2);
                    const cols = [col1, col2, col3];

                    return cols.map((col) => {
                      const colKey = Array.isArray(col) ? col.join("|") : "";

                      return (
                        <div className="col-md-4" key={colKey || "empty-col"}>
                          <FormControl sx={{ m: { xs: 0, md: 1 } }} variant="standard">
                            <FormGroup>
                              {col.map((item: string) => (
                                <FormControlLabel
                                  key={item}
                                  control={
                                    <Checkbox
                                      className="py-1"
                                      checked={(reasonChecked || []).includes(item)}
                                      onChange={handleChange}
                                      name={item}
                                    />
                                  }
                                  label={item}
                                  onClick={onReasonLabelClick}
                                  onMouseDown={onReasonLabelMouseDown}
                                />
                              ))}
                            </FormGroup>
                          </FormControl>
                        </div>
                      );
                    });
                  })()}
                </div>
              </Box>
            </div>
          </div>
        )}
      </div>
      {/* Exit and Continue button row with modal */}
      <div className="flex justify-between items-center pb-4 md:pb-8 px-2 mt-4" id="referral-reason-btn-row">
        <button
          type="button"
          className="exit-btn link-like-btn"
          style={{ cursor: "pointer", background: "none", border: "none", padding: 0, textDecoration: "underline", alignSelf: "flex-start", marginTop: "8px" }}
          onClick={() => setIsOpen(true)}
          aria-label={t("Exit")}
        >
          {t("Exit")}
        </button>
        <Button
          id="SMS"
          type="button"
          data-testid="SMS-Button"
          name="Continue"
          onClick={onContinueHandler}
          value="Continue"
          className="button-class"
          disabled={!hasReferralReasons}
        >
          {t("Continue")}
          <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
        </Button>
      </div>

      <Modal open={isOpen} cssClasses="modalPopUp">
        <div id="exitmodal-div1" className="modalPopUpContainer container my-1">
          <div
            id="exitmodal-div2"
            className="py-lg-0 py-sm-0 h5 my-5 question-font-size text-center global-txt"
          >
            {t("SSR030ai")}
            <br />
            <br />
            {t("SSR030aii")}
          </div>
          <div
            id="exitmodal-div3"
            className="modalButtonContainer button-positions flex flex-wrap text-center pad-bot pt-lg-0"
          >
            <Button id="exitmodal-btn2" className="button-class" onClick={() => setIsOpen(false)}>
              {t("Financial_ReturnToMyReferral")}
              <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
            </Button>
            <Button id="exitmodal-btn3" onClick={exitButtonHandler} className="button-class">
              {t("leave_button")}
              <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
  
};

export default ReferralReason;
